import styled from '@emotion/styled'
import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { StatusText } from 'components/StatusText'
import { ResourceCategory } from 'context'
import { FieldProps } from 'formik'
import { FormikDropdown } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { Participant, ProvingUserRole, User, UserRole } from 'model'
import {
  DayOfWeek,
  Label,
  Link,
  MessageBar,
  Stack,
  Text,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { useResourcesQuery } from 'views/ProvingDetailsView/hooks'

const StyledSectionTitle = styled.div`
  padding: 15px 0 3px 0;
  border-bottom: 1px solid;
`

export const ResourceBookingDetailsForm: React.FC<{
  participants: Participant[]
  orderIds: string[]
  resourceCategories: ResourceCategory[] | undefined
  disableDatePickers?: boolean
}> = ({
  participants,
  orderIds,
  resourceCategories,
  disableDatePickers = false,
}) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const parent: string = initialValues.parent ? initialValues.parent.title : ''
  const parentParent: string =
    initialValues.category === 'office' &&
    initialValues.type === 'workstation' &&
    initialValues.parent.parent
      ? initialValues.parent.parent.title
      : ''
  const category: string = initialValues.category
    ? t(`field.category.${initialValues.category}`)
    : ''
  const type: string = initialValues.type
    ? t(`field.type.${initialValues.type}`)
    : ''
  const { loading, error } = useResourcesQuery({
    variables: {
      provingGroundId: proving!.provingGround!._id,
    },
  })
  const pageGeneral = window.location.href.split('?')[0] + '?page=general'
  const pageProvingGround =
    window.location.origin + '/_admin/data/proving-grounds'
  const auth = useAuthContext()
  const router = useRouter()
  router.setOption('queryParamsMode', 'strict')
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)
  // const resources: ITag[] =
  //   data && !loading
  //     ? data.resources.map(resource => ({
  //         key: resource._id,
  //         name: resource.title,
  //       }))
  //     : []

  // const categoryOptions = resourceCategories
  //   ? [
  //       ...resourceCategories.map(
  //         (item: ResourceCategory) =>
  //           ({
  //             key: item.id,
  //             text: item.title,
  //           } as IDropdownOption)
  //       ),
  //     ]
  //   : []

  // const bla =
  //   resourceCategories &&
  //   resourceCategories.find(x => x.id === initialValues.category)

  // const typeOptions = [
  //   bla &&
  //     bla.types.map(
  //       (item: any) =>
  //         ({
  //           key: item.id,
  //           text: item.title,
  //         } as IDropdownOption)
  //     ),
  // ]

  // const parentOptions =
  //   data && !loading
  //     ? data.resources.map(
  //         (item: any) =>
  //           ({
  //             key: item.id,
  //             text: item.title,
  //           } as IDropdownOption)
  //       )
  //     : []

  const canUpdateCurrentProving = canUpdateProving(proving)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const userCanUpdate = () => {
    if (isMaintenanceHead) {
      return false
    } else if (isProvingManager) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else {
      return false
    }
  }

  return (
    <FlexSpinner loading={loading} error={error}>
      <FormStack>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.provingTitle.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingTitle.label')}:</Label>
              <Box py={1}>
                <Link href={pageGeneral} target="_blank" title="Zur Erprobung">
                  {`${proving.title}, ${proving.id}`}
                </Link>
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingGroundTitle.label')}:</Label>
              <Box py={1}>
                {isAdmin || isResourceManager ? (
                  <Link
                    href={pageProvingGround}
                    target="_blank"
                    title="Zum Standort"
                    onClick={() => {
                      localStorage.setItem(
                        'currentProvingGround',
                        JSON.stringify(proving.provingGround)
                      )
                    }}
                  >
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Link>
                ) : (
                  <Text>
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Text>
                )}
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingStatus.label')}:</Label>
              <Box py={1}>
                <StatusText status={proving!.status || 0} />
              </Box>
            </Stack>
          </Stack>
          {proving.responsible && proving.responsible.length > 0 && (
            <Stack.Item className="composite-field">
              <Label>{t('field.responsible.label')}</Label>
              <Box py={1}>
                <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
                  {proving.responsible && proving.responsible.length > 0
                    ? proving.responsible.map((user: User, i: any) => {
                        return <Text key={i}>{user.name}</Text>
                      })
                    : ''}
                </AutoStack>
              </Box>
            </Stack.Item>
          )}
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.resourceBooking.label')}
          </Text>
        </StyledSectionTitle>
        <>
          {/* <FieldStack>
            <FieldStackItem
              name="category"
              render={fieldProps => (
                <FormikDropdown
                  options={categoryOptions}
                  label={t('field.category.label')}
                  {...fieldProps}
                  disabled={true}
                />
              )}
            />
          </FieldStack>
          {typeOptions && (
            <FieldStack>
              <FieldStackItem
                name="type"
                render={fieldProps => (
                  <FormikDropdown
                    options={typeOptions as any}
                    label={t('field.type.label')}
                    {...fieldProps}
                    disabled={true}
                  />
                )}
              />
            </FieldStack>
          )} */}
          <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
            <Label>Kategorie:</Label>
            <Box py={1}>
              <Text>{category}</Text>
            </Box>
          </Stack>
          <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
            <Label>Typ:</Label>
            <Box py={1}>
              <Text>{type}</Text>
            </Box>
          </Stack>
          {parent && parent !== '' && (
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>Bestandteil von:</Label>
              <Box py={1}>
                {isAdmin || isResourceManager ? (
                  <Link
                    target="_blank"
                    title="Bestandteil von"
                    // to={{pathname:'_admin/data/resources',state:{pgid:proving!.provingGround!._id}}}
                    // to={pathname: '/component', state: {id: '#id'}}
                    onClick={() => {
                      router.navigate('admin.data.resources', {
                        pgid: proving!.provingGround!._id,
                        parentID: initialValues.parent._id,
                        category: initialValues.category,
                        type: initialValues.type,
                        // pgid=5e73329625ead60818b72eb9&category=office&type=workstation
                      })
                    }}
                  >
                    {parent}
                  </Link>
                ) : (
                  <Text>{parent}</Text>
                )}
              </Box>
            </Stack>
          )}
          {parentParent && (
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>Gebäude:</Label>
              <Box py={1}>
                {isAdmin || isResourceManager ? (
                  <Link
                    target="_blank"
                    title="Gebäude"
                    // to={{pathname:'_admin/data/resources',state:{pgid:proving!.provingGround!._id}}}
                    // to={pathname: '/component', state: {id: '#id'}}
                    onClick={() => {
                      router.navigate('admin.data.resources', {
                        pgid: proving!.provingGround!._id,
                        parentID: initialValues.parent.parent._id,
                        category: initialValues.parent.category,
                        type: initialValues.parent.type,
                        // pgid=5e73329625ead60818b72eb9&category=office&type=workstation
                      })
                    }}
                  >
                    {parentParent}
                  </Link>
                ) : (
                  <Text>{parentParent}</Text>
                )}
              </Box>
            </Stack>
          )}
          <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
            <Label>Ressource:</Label>
            <Box py={1}>
              {isAdmin || isResourceManager ? (
                <Link
                  target="_blank"
                  onClick={() => {
                    router.navigate('admin.data.resources', {
                      pgid: proving!.provingGround!._id,
                      id: initialValues.resource,
                      provingsList: true,
                      category: initialValues.category,
                      type: initialValues.type,
                    })
                  }}
                >
                  {initialValues.title}
                </Link>
              ) : (
                <Text>{initialValues.title}</Text>
              )}
            </Box>
          </Stack>

          {/* <FieldStack>
            <FieldStackItem
              name="resource"
              render={fieldProps => (
                <FormikTagPicker
                  tags={resources}
                  label={t('field.resource.label')}
                  {...fieldProps}
                  required={true}
                  disabled={true}
                />
              )}
            />
          </FieldStack> */}
          <FieldStack horizontal={true}>
            <FieldStackItem
              name={'dateFrom'}
              render={(fieldProps) => (
                <FormikGraphQLDatePicker
                  label={'Von'}
                  defaultValue={proving.dateFrom}
                  // minDate={new Date(proving.dateFrom)}
                  // maxDate={new Date(proving.dateTo)}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate as any}
                  {...fieldProps}
                  disabled={!userCanUpdate() || disableDatePickers}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
              )}
            />
            <FieldStackItem
              name={'dateTo'}
              render={(fieldProps) => (
                <FormikGraphQLDatePicker
                  label={'Bis'}
                  defaultValue={proving.dateTo}
                  // minDate={new Date(proving.dateFrom)}
                  // maxDate={new Date(proving.dateTo)}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate as any}
                  {...fieldProps}
                  disabled={!userCanUpdate() || disableDatePickers}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
              )}
            />
          </FieldStack>
          <MessageBar>
            {'Temporäre Ressource, Datumsänderung nicht möglich'}
          </MessageBar>
          <FieldStack>
            <FieldStackItem
              name="participantId"
              render={(fieldProps: FieldProps<any>) => (
                <FormikDropdown
                  label={'Teilnehmer'}
                  {...fieldProps}
                  options={participants
                    .filter((y) => y.person)
                    .map((x) => {
                      return {
                        key: x._id,
                        text: x.person!.name,
                      }
                    })}
                  disabled={!userCanUpdate()}
                />
              )}
            />
          </FieldStack>
        </>
      </FormStack>
    </FlexSpinner>
  )
}
